import React, { useContext, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Divider,
  Box,
  Link,
  Tabs,
  Tab,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ReactMarkdown from "react-markdown";
import { AuthContext } from "../../context/auth-context";

// Set to true when this feature is ready to be shown to users
const IS_DISPLAY = true;

function RawSearchContent({ data }) {
  return (
    <>
      {data?.text && (
        <Box
          sx={{
            "& p": { mt: 0, mb: 1 },
            "& ul, & ol": { pl: 2.5, mb: 1 },
            "& li": { mb: 0.25 },
            "& h1,& h2,& h3,& h4": { mt: 1.5, mb: 0.5 },
            "& strong": { fontWeight: 600 },
            "& a": { color: "primary.main", wordBreak: "break-all" },
            fontSize: "0.875rem",
            lineHeight: 1.6,
          }}
        >
          <ReactMarkdown>{data.text}</ReactMarkdown>
        </Box>
      )}

      {data?.sources?.length > 0 && (
        <>
          {data?.text && <Divider sx={{ my: 1.5 }} />}
          <Typography variant="subtitle2" gutterBottom>
            Sources
          </Typography>
          <Box component="ul" sx={{ pl: 2.5, m: 0 }}>
            {data.sources.map((src, i) => (
              <Box component="li" key={i} sx={{ mb: 0.25 }}>
                <Link
                  href={src}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ fontSize: "0.8rem", wordBreak: "break-all" }}
                >
                  {src}
                </Link>
              </Box>
            ))}
          </Box>
        </>
      )}
    </>
  );
}

/**
 * RawSearchInfoButton
 *
 * Props:
 *   data: { grok: {text,sources}|null, gemini: {text,sources}|null, consolidated: {text,sources}|null }
 *     — the multi-LLM shape. `consolidated` is a single unified narrative with no trace that
 *     it was derived from more than one provider. For backward compatibility, a flat
 *     { text, sources } (pre-multi-LLM history rows) is also accepted and treated as Grok-only
 *     (no consolidated/gemini tabs for those rows).
 *   label: string — optional dialog title (default "Search Info")
 *
 * Shows a tab switcher whenever more than one of {Consolidated, Grok, Gemini} has content;
 * otherwise renders today's single-view with no tabs. Admins get all three simultaneously —
 * this is not an exclusive switch, just three views of the same underlying research.
 * Only renders when IS_DISPLAY is true AND the current user is admin (unchanged — regular/
 * end-user visibility is deferred to a future change gated by is_raw_llm_view_enabled).
 */
function RawSearchInfoButton({ data, label = "Search Info" }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState(() => {
    const isMultiLlmShapeInitial = data && ("grok" in data || "gemini" in data || "consolidated" in data);
    const consolidatedInitial = isMultiLlmShapeInitial ? data.consolidated : null;
    return consolidatedInitial?.text || consolidatedInitial?.sources?.length ? "consolidated" : "grok";
  });
  const { user } = useContext(AuthContext);
  const isCurrentUserAdmin = user?.user_role?.role?.name === "Company admin";

  if (!IS_DISPLAY || !isCurrentUserAdmin) return null;

  const isMultiLlmShape = data && ("grok" in data || "gemini" in data || "consolidated" in data);
  const grok = isMultiLlmShape ? data.grok : data;
  const gemini = isMultiLlmShape ? data.gemini : null;
  const consolidated = isMultiLlmShape ? data.consolidated : null;

  const hasGrok = !!(grok?.text || grok?.sources?.length);
  const hasGemini = !!(gemini?.text || gemini?.sources?.length);
  const hasConsolidated = !!(consolidated?.text || consolidated?.sources?.length);
  if (!hasGrok && !hasGemini && !hasConsolidated) return null;

  const dataByTab = { consolidated, grok, gemini };
  const availableTabs = [
    hasConsolidated && { value: "consolidated", label: "Consolidated" },
    hasGrok && { value: "grok", label: "Grok" },
    hasGemini && { value: "gemini", label: "Gemini" },
  ].filter(Boolean);

  const showTabs = availableTabs.length > 1;
  const activeTabValue = showTabs ? tab : availableTabs[0]?.value;
  const activeData = dataByTab[activeTabValue];

  return (
    <>
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        sx={{ ml: 0.5, color: "text.secondary" }}
        title={label}
      >
        <InfoOutlinedIcon fontSize="small" />
      </IconButton>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
        onClick={(e) => e.stopPropagation()}
      >
        <DialogTitle sx={{ pb: showTabs ? 0 : 1 }}>{label}</DialogTitle>
        {showTabs && (
          <Tabs
            value={tab}
            onChange={(e, newTab) => setTab(newTab)}
            sx={{ px: 3, minHeight: 36 }}
          >
            {availableTabs.map(({ value, label: tabLabel }) => (
              <Tab key={value} value={value} label={tabLabel} sx={{ minHeight: 36, py: 0.5 }} />
            ))}
          </Tabs>
        )}
        <Divider />
        <DialogContent>
          <RawSearchContent data={activeData} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default RawSearchInfoButton;
